import { useEffect, useRef, useState } from "react"
import { t } from "i18next"
import { LocalOverlay, overlayFromFile, overlayFromMyMapsUrl, OVERLAY_PALETTE } from "./localOverlays"
import { CollarStyle, MapProjection, Basemap, BASEMAPS } from "./Map"
import { checkLoggedIn } from "./session"
import "./BurgerMenu.css"

const ACCOUNT_HOME_URL = "https://account.nofence.no/account/home/"

function BurgerMenu({
  overlays,
  setOverlays,
  collarStyle,
  setCollarStyle,
  projection,
  setProjection,
  basemap,
  setBasemap,
  collarOpacity,
  setCollarOpacity,
}: {
  overlays: LocalOverlay[]
  setOverlays: React.Dispatch<React.SetStateAction<LocalOverlay[]>>
  collarStyle: CollarStyle
  setCollarStyle: React.Dispatch<React.SetStateAction<CollarStyle>>
  projection: MapProjection
  setProjection: React.Dispatch<React.SetStateAction<MapProjection>>
  basemap: Basemap
  setBasemap: React.Dispatch<React.SetStateAction<Basemap>>
  collarOpacity: number
  setCollarOpacity: React.Dispatch<React.SetStateAction<number>>
}) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string>()
  const [linkUrl, setLinkUrl] = useState("")
  const [loadingLink, setLoadingLink] = useState(false)
  const [loggedIn, setLoggedIn] = useState(false) // Nofence account cookie present
  const menuRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Detect a logged-in Nofence session; upload/link tools are staff-only.
  useEffect(() => {
    let alive = true
    checkLoggedIn().then((v) => {
      if (alive) setLoggedIn(v)
    })
    return () => {
      alive = false
    }
  }, [])

  // Close when clicking outside the menu.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || !fileList.length) return
    setError(undefined)
    const loaded: LocalOverlay[] = []
    const failed: string[] = []
    // Rotate the palette so each new file gets a distinct default color.
    let colorIdx = overlays.length
    for (const file of Array.from(fileList)) {
      try {
        loaded.push(await overlayFromFile(file, OVERLAY_PALETTE[colorIdx % OVERLAY_PALETTE.length]))
        colorIdx++
      } catch (e) {
        failed.push(`${file.name}: ${e instanceof Error ? e.message : "failed"}`)
      }
    }
    if (loaded.length) setOverlays((prev) => [...prev, ...loaded])
    if (failed.length) setError(failed.join("\n"))
    if (fileInputRef.current) fileInputRef.current.value = "" // allow re-selecting same file
  }

  async function handleAddLink() {
    const url = linkUrl.trim()
    if (!url || loadingLink) return
    setError(undefined)
    setLoadingLink(true)
    try {
      const colorIdx = overlays.length
      const overlay = await overlayFromMyMapsUrl(url, OVERLAY_PALETTE[colorIdx % OVERLAY_PALETTE.length])
      setOverlays((prev) => [...prev, overlay])
      setLinkUrl("")
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load map link")
    } finally {
      setLoadingLink(false)
    }
  }

  function toggleVisible(id: string) {
    setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, visible: !o.visible } : o)))
  }

  function removeOverlay(id: string) {
    setOverlays((prev) => prev.filter((o) => o.id !== id))
  }

  function setColor(id: string, color: string) {
    setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, color } : o)))
  }

  function setOpacity(id: string, opacity: number) {
    setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, opacity } : o)))
  }

  return (
    <div className="BurgerMenu" ref={menuRef}>
      <button
        className="BurgerButton"
        aria-label={t("menu.open")}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span />
        <span />
        <span />
      </button>

      {open && (
        <div className="BurgerDropdown">
          <a
            className="BurgerItem BurgerLink"
            href={ACCOUNT_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("menu.staffLogin")}
          </a>

          <div className="BurgerDivider" />

          <button
            className="BurgerItem"
            onClick={() => setCollarStyle((s) => (s === "icon" ? "circle" : "icon"))}
          >
            {collarStyle === "icon" ? t("menu.showCircles") : t("menu.showIcons")}
          </button>

          <div className="BurgerSlider">
            <label className="BurgerSliderLabel">
              {t("menu.collarOpacity")}
              <span className="BurgerSliderValue">{Math.round(collarOpacity * 100)}%</span>
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={collarOpacity}
              aria-label={t("menu.collarOpacity")}
              onChange={(e) => setCollarOpacity(Number(e.target.value))}
            />
          </div>

          <button
            className="BurgerItem"
            onClick={() => setProjection((p) => (p === "globe" ? "mercator" : "globe"))}
          >
            {projection === "globe" ? t("menu.showFlat") : t("menu.showGlobe")}
          </button>

          <div className="BurgerDivider" />

          <p className="BurgerSectionLabel">{t("menu.basemap")}</p>
          {BASEMAPS.map((b) => (
            <button
              key={b}
              className={`BurgerItem${basemap === b ? " BurgerItemActive" : ""}`}
              aria-pressed={basemap === b}
              onClick={() => setBasemap(b)}
            >
              {t(`menu.basemaps.${b}`)}
            </button>
          ))}

          {loggedIn && (
            <>
              <div className="BurgerDivider" />

              <button className="BurgerItem" onClick={() => fileInputRef.current?.click()}>
                {t("menu.uploadFiles")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />

              <div className="BurgerDivider" />

              <p className="BurgerSectionLabel">{t("menu.mapsLink")}</p>
              <div className="BurgerLinkRow">
                <input
                  type="url"
                  className="BurgerLinkInput"
                  placeholder={t("menu.mapsLinkPlaceholder")}
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddLink()
                  }}
                />
                <button
                  className="BurgerLinkAdd"
                  disabled={!linkUrl.trim() || loadingLink}
                  onClick={handleAddLink}
                >
                  {loadingLink ? t("menu.mapsLinkLoading") : t("menu.mapsLinkAdd")}
                </button>
              </div>

              {error && <p className="BurgerError">{error}</p>}
            </>
          )}

          {overlays.length > 0 && (
            <>
              <div className="BurgerDivider" />
              <ul className="BurgerFileList">
                {overlays.map((o) => (
                  <li key={o.id} className="BurgerFile">
                    <div className="BurgerFileRow">
                      <label className="BurgerFileLabel" title={o.name}>
                        <input
                          type="checkbox"
                          checked={o.visible}
                          onChange={() => toggleVisible(o.id)}
                        />
                        <span className="BurgerFileName">{o.name}</span>
                      </label>
                      <input
                        type="color"
                        className="BurgerColor"
                        value={o.color}
                        aria-label={t("menu.fileColor")}
                        title={t("menu.fileColor")}
                        onChange={(e) => setColor(o.id, e.target.value)}
                      />
                      <button
                        className="BurgerRemove"
                        aria-label={t("menu.remove")}
                        onClick={() => removeOverlay(o.id)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="BurgerSlider">
                      <label className="BurgerSliderLabel">
                        {t("menu.opacity")}
                        <span className="BurgerSliderValue">{Math.round(o.opacity * 100)}%</span>
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={o.opacity}
                        aria-label={t("menu.opacity")}
                        onChange={(e) => setOpacity(o.id, Number(e.target.value))}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default BurgerMenu
